import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api";
import { captureProfileGuard } from "./profileSession";

type OverviewRead = (signal: AbortSignal, assertCurrent: () => void) => Promise<void>;
type ReadOptions = { signal?: AbortSignal; background?: boolean };
type Attempt = { controller: AbortController; identity: string };

export function ignoreOverviewAbort(failure: unknown): void {
  if (!(failure instanceof DOMException && failure.name === "AbortError")) throw failure;
}

/** List-level access refusals must remove the affected private projection;
 * temporary network/provider failures must leave the last readable data intact. */
export function overviewAccessLost(failure: unknown): boolean {
  return failure instanceof ApiError && ([401, 403, 404].includes(failure.status)
    || failure.code === "invalid_profile" || failure.code === "profile_unavailable");
}

/** A refused selected profile invalidates every dataset in that owner, even if
 * another request completed just before the profile became unavailable. */
export function overviewProfileLost(failure: unknown): boolean {
  return failure instanceof ApiError && (failure.status === 401
    || failure.code === "invalid_profile" || failure.code === "profile_unavailable");
}

export function overviewFailureKey(failures: unknown[]): string {
  if (failures.some(failure => failure instanceof ApiError && failure.status === 401)) return "refresh.sessionExpired";
  return failures.some(overviewAccessLost) ? "refresh.accessLost" : "refresh.overviewPartial";
}

/** Contracts, Wallet and Earnings share read ownership, not business datasets.
 * Foreground reads replace prior work; polls yield to it. Owners must assert
 * freshness immediately before any state commit, including after allSettled. */
export function useOverviewRead(identity: string) {
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const attempt = useRef<Attempt | null>(null);
  const mounted = useRef(true);
  const [reading, setReading] = useState(false);
  const cancel = useCallback(() => {
    const previous = attempt.current;
    attempt.current = null;
    previous?.controller.abort();
    if (mounted.current) setReading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; cancel(); };
  }, [cancel]);
  useEffect(() => { cancel(); return cancel; }, [identity, cancel]);

  const run = useCallback(async (read: OverviewRead, options: ReadOptions = {}) => {
    if (options.background && attempt.current) return;
    cancel();
    const active: Attempt = { identity: currentIdentity.current, controller: new AbortController() };
    const assertProfileCurrent = captureProfileGuard();
    const abort = () => { if (attempt.current === active) cancel(); };
    const assertCurrent = () => {
      assertProfileCurrent();
      if (!mounted.current || attempt.current !== active || active.controller.signal.aborted
        || currentIdentity.current !== active.identity) throw new DOMException("Overview read was superseded.", "AbortError");
    };
    attempt.current = active;
    setReading(true);
    options.signal?.addEventListener("abort", abort, { once: true });
    try {
      if (options.signal?.aborted) abort();
      assertCurrent();
      await read(active.controller.signal, assertCurrent);
    } finally {
      options.signal?.removeEventListener("abort", abort);
      if (attempt.current === active) {
        attempt.current = null;
        if (mounted.current) setReading(false);
      }
    }
  }, [cancel]);
  return { run, cancel, reading };
}
